'use client';

import { useMemo, useState } from 'react';

import {
  CONCIERGE_CTA_CARD_IMAGE,
  CONCIERGE_SERVICE_FALLBACK_IMAGE,
  ConciergeHubPage,
  getConciergeServiceImageSrc,
  hasConciergeServiceImage,
} from '@/lib/entities/concierge';
import { whatsappTextHello } from '@/lib/entities/social';

import { Button, CircleButton } from '@/lib/shared/components/Buttons';
import { H2, H3, P3, P4 } from '@/lib/shared/components/Typography';
import { ArrowIcon, SearchIcon } from '@/lib/shared/icons';

import clsx from 'clsx';
import Image from 'next/image';
import Link from 'next/link';

import styles from './ConciergePages.module.scss';
import { openConciergeLeadModal } from './openConciergeLeadModal';

type ConciergeCatalogSectionProps = {
  page: ConciergeHubPage;
  locale: 'ru' | 'en';
};

const normalizeSearchQuery = (value: string) => value.trim().toLowerCase();

const openConciergeLeadForm = (sourceForm: string, comment?: string) => {
  openConciergeLeadModal({ sourceForm, comment });
};

export const ConciergeCatalogSection = ({
  page,
  locale,
}: ConciergeCatalogSectionProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const normalizedQuery = normalizeSearchQuery(searchQuery);
  const whatsappUrl = whatsappTextHello[locale];

  const filteredCards = useMemo(
    () =>
      page.cards.filter((card) => {
        if (!normalizedQuery) return true;

        return [card.title, card.description, card.price].some((field) =>
          field.toLowerCase().includes(normalizedQuery)
        );
      }),
    [normalizedQuery, page.cards]
  );

  return (
    <section className={styles.rootCatalog} id="concierge-catalog">
      <div className={styles.rootCatalog__head}>
        <H2 className={styles.rootCatalog__title}>{page.catalogTitle}</H2>
        <label className={styles.rootCatalog__search}>
          <input
            type="search"
            name="concierge-catalog-search"
            value={searchQuery}
            placeholder={page.searchPlaceholder}
            aria-label={page.searchPlaceholder}
            className={styles.rootCatalog__searchInput}
            onChange={(event) => setSearchQuery(event.target.value)}
          />
          <span className={styles.rootCatalog__searchIcon} aria-hidden>
            <SearchIcon />
          </span>
        </label>
        <P3 className={styles.rootCatalog__text}>{page.catalogText}</P3>
      </div>

      <div className={styles.rootCatalog__grid}>
        {filteredCards.map((card) => {
          const accent = card.accent || 'ocean';
          const hasImage = hasConciergeServiceImage(card.imageKey);
          const imageSrc = hasImage
            ? getConciergeServiceImageSrc(card.imageKey)
            : CONCIERGE_SERVICE_FALLBACK_IMAGE;
          const cardHref = card.href || '/concierge';
          const isExternalHref = /^https?:\/\//.test(cardHref);
          const shouldOpenLeadForm = Boolean(card.opensLeadForm);
          const leadFormSource = `concierge_hub_${card.imageKey}`;

          return (
            <article className={styles.rootTile} key={card.imageKey}>
              {shouldOpenLeadForm ? (
                <button
                  type="button"
                  onClick={() =>
                    openConciergeLeadForm(leadFormSource, card.title)
                  }
                  className={styles.rootTile__link}
                  aria-label={card.title}
                />
              ) : isExternalHref ? (
                <a
                  href={cardHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.rootTile__link}
                />
              ) : (
                <Link href={cardHref} className={styles.rootTile__link} />
              )}
              <div
                className={clsx(
                  styles.rootTile__media,
                  styles[`rootTile__media--${accent}`],
                  styles['rootTile__media--hasImage']
                )}
              >
                <Image
                  src={imageSrc}
                  alt={card.title}
                  fill
                  sizes="(min-width: 996px) 33vw, (min-width: 768px) 50vw, 100vw"
                  loader={({ src }) => src}
                  className={styles.rootTile__image}
                />
              </div>
              <div className={styles.rootTile__body}>
                <div className={styles.rootTile__copy}>
                  <H3 className={styles.rootTile__title}>{card.title}</H3>
                  <P3 className={styles.rootTile__description}>
                    {card.description}
                  </P3>
                </div>
                <div className={styles.rootTile__divider} aria-hidden />
                <div className={styles.rootTile__bottom}>
                  <p className={styles.rootTile__price}>{card.price}</p>
                  {shouldOpenLeadForm ? (
                    <CircleButton
                      as="button"
                      type="button"
                      onClick={() =>
                        openConciergeLeadForm(leadFormSource, card.title)
                      }
                      theme="mixed"
                      name={card.title}
                      className={styles.rootTile__arrow}
                      content={<ArrowIcon isRight />}
                    />
                  ) : isExternalHref ? (
                    <CircleButton
                      as="a"
                      href={cardHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      theme="mixed"
                      name={card.title}
                      className={styles.rootTile__arrow}
                      content={<ArrowIcon isRight />}
                    />
                  ) : (
                    <CircleButton
                      as={Link}
                      href={cardHref}
                      theme="mixed"
                      name={card.title}
                      className={styles.rootTile__arrow}
                      content={<ArrowIcon isRight />}
                    />
                  )}
                </div>
              </div>
            </article>
          );
        })}

        <article className={styles.rootTileCta}>
          <div className={styles.rootTileCta__decor} aria-hidden>
            <div className={styles.rootTileCta__decorFrame}>
              <div className={styles.rootTileCta__decorRotate}>
                <div className={styles.rootTileCta__decorMedia}>
                  <Image
                    src={CONCIERGE_CTA_CARD_IMAGE}
                    alt=""
                    width={648}
                    height={264}
                    sizes="(min-width: 996px) 648px, 100vw"
                    loader={({ src }) => src}
                    className={styles.rootTileCta__decorImage}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className={styles.rootTileCta__content}>
            <H2 as="h3" className={styles.rootTileCta__title}>
              {page.ctaCard.title}
            </H2>
            <Button
              as="a"
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              label={page.ctaCard.buttonLabel}
              size="lg"
              theme="light"
              className={styles.rootTileCta__button}
            />
          </div>
        </article>
      </div>

      {normalizedQuery && filteredCards.length === 0 ? (
        <P3 className={styles.rootCatalog__empty}>{page.searchEmpty}</P3>
      ) : null}

      {page.catalogDisclaimer ? (
        <P4 className={styles.rootCatalog__hubDisclaimer}>
          {page.catalogDisclaimer}
        </P4>
      ) : null}
    </section>
  );
};
